import { asList, isDeletedUser } from 'shared/utilities/format';

export const PLAN_PURCHASE_STATUSES = ['pending_payment', 'paid', 'failed', 'cancelled', 'refunded'];
export const ENTITLEMENT_STATUSES = ['active', 'exhausted', 'expired', 'suspended', 'revoked'];
export const ACCESS_CODE_STATUSES = ['active', 'claimed', 'completed', 'revoked', 'expired'];
export const COMPANY_STATUSES = ['active', 'suspended'];
export const COMPANY_ROLES = ['owner', 'manager', 'viewer'];
export const PAYMENT_PROVIDERS = ['manual'];

export const SENSITIVE_CODE_KEYS = [
  'code',
  'plaintext',
  'plain_code',
  'full_code',
  'code_encrypted',
  'code_hash',
  'access_code',
];

export function listItems(data) {
  return asList(data);
}

export function listMeta(data) {
  return data?.meta || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: asList(data).length,
  };
}

export function statusValue(value) {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return value.value || value.status || String(value);
}

export function readNested(entity, path, fallback = undefined) {
  if (!entity || typeof entity !== 'object') {
    return fallback;
  }
  const value = path.split('.').reduce((current, key) => (current == null ? current : current[key]), entity);
  return value === undefined ? fallback : value;
}

export function readCredit(entity, key) {
  if (!entity || typeof entity !== 'object') {
    return undefined;
  }
  if (entity[key] != null && entity[key] !== '') {
    return entity[key];
  }
  if (entity.credits && entity.credits[key] != null && entity.credits[key] !== '') {
    return entity.credits[key];
  }
  if (entity.credit_totals && entity.credit_totals[key] != null && entity.credit_totals[key] !== '') {
    return entity.credit_totals[key];
  }
  return undefined;
}

export function displayCredit(entity, key) {
  const value = readCredit(entity, key);
  return value == null || value === '' ? '—' : String(value);
}

export function hasHistoricalPurchases(plan) {
  if (!plan) {
    return false;
  }
  if (typeof plan.has_historical_purchases === 'boolean') {
    return plan.has_historical_purchases;
  }
  if (typeof plan.has_purchases === 'boolean') {
    return plan.has_purchases;
  }
  const count = Number(plan.purchases_count);
  return Number.isFinite(count) && count > 0;
}

export function canDestroyPlan(plan) {
  if (!plan) {
    return true;
  }
  if (typeof plan.can_delete === 'boolean') {
    return plan.can_delete;
  }
  return !hasHistoricalPurchases(plan);
}

export function canConfirmPurchasePayment(purchase) {
  if (!purchase) {
    return false;
  }
  if (typeof purchase.can_confirm_payment === 'boolean') {
    return purchase.can_confirm_payment;
  }
  return statusValue(purchase.status) === 'pending_payment'
    && String(purchase.payment_provider || purchase.payment_mode || 'manual') === 'manual';
}

export function entitlementActionAllowed(entitlement, action) {
  const allowed = entitlement?.allowed_actions;
  if (Array.isArray(allowed)) {
    return allowed.includes(action);
  }
  const status = statusValue(entitlement?.status);
  if (action === 'suspend') {
    return status === 'active';
  }
  if (action === 'resume' || action === 'restore') {
    return status === 'suspended';
  }
  if (action === 'revoke') {
    return status !== 'revoked';
  }
  return false;
}

export function canRevokeAccessCode(code) {
  if (!code) {
    return false;
  }
  if (typeof code.can_revoke === 'boolean') {
    return code.can_revoke;
  }
  return statusValue(code.status) === 'active' && !code.claimed_by_user_id && !code.attempt_id;
}

export function revokeBlockedReasonKey(code) {
  const status = statusValue(code?.status);
  if (status === 'claimed') {
    return 'companyPlans.codes.cannotRevokeClaimed';
  }
  if (status === 'completed') {
    return 'companyPlans.codes.cannotRevokeCompleted';
  }
  if (status === 'revoked') {
    return 'companyPlans.codes.alreadyRevoked';
  }
  if (status === 'expired') {
    return 'companyPlans.codes.cannotRevokeExpired';
  }
  return 'companyPlans.codes.cannotRevoke';
}

export function maskedCode(code) {
  return code?.code_masked || code?.masked || '—';
}

export function mayRevealFullCode(code) {
  return Boolean(code?.can_reveal_code || code?.reveal_plaintext);
}

export function displayedCodeValue(code) {
  if (mayRevealFullCode(code) && typeof code?.plaintext === 'string' && code.plaintext) {
    return code.plaintext;
  }
  return maskedCode(code);
}

export function stripSensitiveCodeFields(value) {
  if (Array.isArray(value)) {
    return value.map(stripSensitiveCodeFields);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  const next = {};
  Object.entries(value).forEach(([key, child]) => {
    if (SENSITIVE_CODE_KEYS.includes(key)) {
      return;
    }
    next[key] = stripSensitiveCodeFields(child);
  });
  return next;
}

export function assertCodeNotPersisted() {
  const storage = [window.localStorage, window.sessionStorage];
  storage.forEach((store) => {
    if (!store) {
      return;
    }
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      const stored = String(store.getItem(key) || '');
      if (/PACILO-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}/.test(stored) && !stored.includes('****') && !stored.includes('XXXX')) {
        throw new Error('Access codes must not be stored in browser storage.');
      }
    }
  });
}

export function companyDisplayName(company) {
  if (!company) {
    return '—';
  }
  return company.display_name || company.legal_name || company.name || '—';
}

export function planDisplayName(plan, locale = 'en') {
  if (!plan) {
    return '—';
  }
  if (locale === 'ar') {
    return plan.name_ar || plan.plan_name_ar || plan.name_en || plan.plan_name_en || '—';
  }
  return plan.name_en || plan.plan_name_en || plan.name_ar || plan.plan_name_ar || '—';
}

export function memberLabel(member, t) {
  const user = member?.user || member;
  if (isDeletedUser(user)) {
    return t('users.deleted');
  }
  return user?.name || user?.full_name || t('users.deleted');
}

export function purchaseSnapshot(purchase) {
  return purchase?.plan_snapshot || {
    name_ar: purchase?.plan_name_ar,
    name_en: purchase?.plan_name_en,
    description_ar: purchase?.plan_description_ar,
    description_en: purchase?.plan_description_en,
    attempt_quantity: purchase?.attempt_quantity,
    unit_price_minor: purchase?.unit_price_minor,
    currency: purchase?.currency,
    validity_days: purchase?.validity_days,
    terms_ar: purchase?.terms_ar,
    terms_en: purchase?.terms_en,
  };
}

export function sanitizePatternForDisplay(pattern = {}) {
  const {
    raw_score: _rawScore,
    raw_score_basis_points: _rawScoreBp,
    weight: _weight,
    weights: _weights,
    scoring_bounds: _bounds,
    matching: _matching,
    match_score: _matchScore,
    answer_weights: _answerWeights,
    configuration_status: _config,
    ...safe
  } = pattern;
  return safe;
}

export function sanitizeResultForDisplay(result) {
  if (!result || typeof result !== 'object') {
    return result;
  }
  return {
    ...result,
    patterns: Array.isArray(result.patterns) ? result.patterns.map(sanitizePatternForDisplay) : result.patterns,
    top_patterns: Array.isArray(result.top_patterns)
      ? result.top_patterns.map(sanitizePatternForDisplay)
      : result.top_patterns,
  };
}

export function resultHasHiddenInternals(container) {
  const haystack = JSON.stringify(container || {});
  return /raw_score|answer_weights|scoring_bounds|match_score/.test(haystack);
}

export function statusTone(status) {
  const value = statusValue(status);
  if (['paid', 'active', 'completed', 'featured'].includes(value)) {
    return 'success';
  }
  if (['pending_payment', 'claimed', 'exhausted', 'expired'].includes(value)) {
    return 'warning';
  }
  if (['failed', 'cancelled', 'refunded', 'suspended', 'revoked'].includes(value)) {
    return 'danger';
  }
  return 'neutral';
}

export function queryFilters(filters = {}) {
  const params = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    params[key] = value;
  });
  return params;
}

export function mapPurchaseConfirmErrors(errors = {}) {
  const mapped = { ...errors };
  if (mapped.reference && !mapped.payment_reference) {
    mapped.payment_reference = mapped.reference;
  }
  if (mapped.note && !mapped.payment_confirmation_note) {
    mapped.payment_confirmation_note = mapped.note;
  }
  return mapped;
}

export async function invalidateCompanyPlanQueries(queryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['company-plans'] }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'overview'] }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'attempts'] }),
  ]);
}
